<template>
    <a-cascader :getPopupContainer="(triggerNode) => triggerNode.parentNode" v-model:value="state.cascaderValue"
        @change="handleChange" placeholder="请选择受理机关" :field-names="{ label: 'name', value: 'id', children: 'children' }"
        :options="state.options" :load-data="loadData"></a-cascader>
</template>

<script lang="ts" setup>
import { reactive, watch, onMounted, nextTick } from 'vue'
import { message } from 'ant-design-vue'
import baseService from '@/utils/http/axios'

const props = defineProps({
    value: {
        type: [String, Object],
        default: () => ({})
    },
    width: {
        type: String,
        default: '300px'
    },
    type: {
        type: Number,
        default: 1
    }
})

const emit = defineEmits(['update:value', 'change'])

interface OptionItem {
    id: string | number
    name: string
    children?: OptionItem[]
    isLeaf?: boolean
    loading?: boolean
}

interface State {
    options: OptionItem[]
    cascaderValue: (string | number)[]
}

const state = reactive<State>({
    options: [],
    cascaderValue: []
})

// 监听 value 变化
watch(
    () => props.value,
    (val) => {
        // 如果 value 为空，清空级联选择器
        if (!val || (typeof val === 'object' && Object.keys(val).length === 0)) {
            state.cascaderValue = []
            return
        }

        // 如果 value 是对象，提取 glj1, glj2, gls
        let list: (string | number)[] = []
        if (typeof val === 'object') {
            list = [val.glj1, val.glj2, val.gls].filter(item => item !== undefined && item !== null && item !== '')
        } else if (typeof val === 'string') {
            // 如果是字符串，尝试解析
            try {
                const parsed = JSON.parse(val)
                if (typeof parsed === 'object') {
                    list = [parsed.glj1, parsed.glj2, parsed.gls].filter(item => item !== undefined && item !== null && item !== '')
                }
            } catch {
                // 如果不是 JSON，按逗号分割
                list = val.split(',').filter(item => item !== '')
            }
        }

        // 如果和当前值相同，跳过，防止死循环
        if (JSON.stringify(state.cascaderValue) === JSON.stringify(list)) {
            return
        }

        state.cascaderValue = list

        // 如果列表不为空，自动加载对应的数据
        if (list.length > 0) {
            nextTick(async () => {
                await loadDataByValues(list)
            })
        }
    },
    { immediate: true, deep: true }
)

onMounted(() => {
    getByLevel()
})

// 根据值列表加载数据
const loadDataByValues = async (values: (string | number)[]) => {
    if (!values || values.length === 0) return

    let currentOptions = state.options
    let found = true

    for (let i = 0; i < values.length; i++) {
        const id = values[i]
        const target = currentOptions.find((item: any) => item.id === id)

        if (!target) {
            found = false
            break
        }

        // 如果不是最后一级，且没有 children，加载数据
        if (i < values.length - 1) {
            if (!target.children || target.children.length === 0) {
                target.loading = true
                try {
                    const res = await baseService({
                        url: '/admin/v1/regOrgan?curOrgan=' + target.id,
                        method: 'get'
                    })
                    target.children = res.data.map((item: any) => {
                        item.isLeaf = i + 1 >= values.length - 1
                        return item
                    })
                    target.loading = false
                    state.options = [...state.options]
                } catch (err) {
                    target.loading = false
                    console.error('加载数据失败:', err)
                }
            }
            currentOptions = target.children || []
        } else {
            // 最后一级，标记为叶子节点
            target.isLeaf = true
        }
    }
}

// 处理级联选择器的 change 事件
const handleChange = (idList: (string | number)[], value: any) => {
    const obj = {
        glj1: idList[0] || '', // 市
        glj2: idList[1] || '', // 区
        gls: idList[2] || ''   // 所
    }
    emit('update:value', obj)
    emit('change', obj)
}

// 根据级别获取数据
const getByLevel = () => {
    const requestParam = {
        url: '/admin/v1/regOrgan',
        method: 'get'
    }
    baseService(requestParam)
        .then((res) => {
            state.options = res.data.map((item: any) => {
                item.isLeaf = false
                return item
            })
        })
        .catch((res) => {
            if (res.msg) {
                message.error(res.msg)
            } else {
                message.error('获取失败')
            }
        })
}

// 加载数据（级联选择器的 load-data 回调）
const loadData = async (selectedOptions: any[]) => {
    if (!selectedOptions || selectedOptions.length === 0) return

    const targetOption = selectedOptions[selectedOptions.length - 1]
    if (!targetOption) return

    // 如果已经有 children，不重复加载
    if (targetOption.children && targetOption.children.length > 0) {
        return
    }

    targetOption.loading = true

    try {
        const res = await baseService({
            url: '/admin/v1/regOrgan?curOrgan=' + targetOption.id,
            method: 'get'
        })

        targetOption.children = res.data.map((item: any) => {
            // 如果已经是第三级（市->区->所），设为叶子节点
            if (selectedOptions.length >= 2) {
                item.isLeaf = true
            } else {
                item.isLeaf = false
            }
            return item
        })

        targetOption.loading = false
        state.options = [...state.options]
    } catch (err: any) {
        targetOption.loading = false
        const msg = err?.msg || err?.message || '加载失败'
        message.error(msg)
    }
}
</script>

<style scoped>
/* 可以添加自定义样式 */
</style>
